import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import type { ColumnDef, ColumnFiltersState, PaginationState, SortingState } from "@tanstack/react-table";
import { format } from "date-fns";
import {
  ArrowRight,
  Download,
  Eye,
  FileText,
  Filter,
  Info,
  Landmark,
  Layers,
  Lock,
  PiggyBank,
  Wallet,
} from "lucide-react";

import DataTable from "@/components/datatable/Datatable";
import DataTableColumnHeader from "@/components/datatable/DataTableColumnHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/auth.context";

type ShareActivityKind = "contribution" | "saving";
type ShareStatus = "Approved" | "Pending" | "Rejected";

type ShareActivity = {
  id: string;
  date: string;
  description: string;
  type: ShareActivityKind;
  shares: number;
  amount: number;
  source: string;
  status: ShareStatus;
  approvedBy: string;
  reference: string;
  rawDate: number;
};

const APPROVED_STATUSES = [
  "verified",
  "completed",
  "approved",
  "deducted_from_salary",
  "submitted_to_hr",
  "confirmed_by_accountant",
  "approved_by_president",
  "verified_by_secretary",
  "confirmed",
];

const REJECTED_STATUSES = ["cancelled", "canceled", "rejected", "failed"];

function asNumber(value: unknown): number {
  if (value === null || value === undefined || value === "") return 0;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
}

function normalizeStatus(status: unknown): ShareStatus {
  const normalized = String(status || "").toLowerCase().replace(/\s+/g, "_");
  if (APPROVED_STATUSES.includes(normalized)) return "Approved";
  if (REJECTED_STATUSES.includes(normalized)) return "Rejected";
  return "Pending";
}

function isSalaryContribution(item: any): boolean {
  const text = [
    item?.source,
    item?.paymentMethod,
    item?.contributionType,
    item?.type,
    item?.description,
    item?.category,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return text.includes("salary_deduction") || text.includes("salary deduction") || text.includes("payroll");
}

function isVoluntarySaving(item: any): boolean {
  const text = [
    item?.source,
    item?.paymentMethod,
    item?.contributionType,
    item?.type,
    item?.description,
    item?.category,
    item?.notes,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return (
    !isSalaryContribution(item) &&
    (text.includes("manual_member_contribution") ||
      text.includes("saving") ||
      text.includes("voluntary") ||
      text.includes("deposit"))
  );
}

function formatMoney(value: number): string {
  return `${Math.round(value || 0).toLocaleString()} FRW`;
}

function formatShares(value: number): string {
  const rounded = Math.round((value || 0) * 100) / 100;
  return Number.isInteger(rounded)
    ? rounded.toLocaleString()
    : rounded.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function formatDateLabel(value: unknown): string {
  const date = value ? new Date(String(value)) : new Date();
  if (Number.isNaN(date.getTime())) return "-";
  return format(date, "dd MMM yyyy");
}

function getActivityDate(item: any): string {
  return (
    item?.paymentDate ||
    item?.contributionDate ||
    item?.deductionDate ||
    item?.paidAt ||
    item?.createdAt ||
    item?.updatedAt ||
    new Date().toISOString()
  );
}

function getApprovedBy(item: any): string {
  return (
    item?.completedBy ||
    item?.deductedBy ||
    item?.submittedToHRBy ||
    item?.accountantConfirmedBy ||
    item?.presidentApprovedBy ||
    item?.secretaryVerifiedBy ||
    item?.verifiedBy ||
    item?.approvedBy ||
    "-"
  );
}

function exportRows(rows: ShareActivity[]) {
  const header = ["Date", "Description", "Type", "Shares", "Amount (FRW)", "Source", "Status", "Approved By", "Reference"];
  const csvRows = rows.map((row) => [
    row.date,
    row.description,
    row.type === "saving" ? "Savings" : "Contribution",
    formatShares(row.shares),
    Math.round(row.amount).toString(),
    row.source,
    row.status,
    row.approvedBy,
    row.reference,
  ]);
  const csv = [header, ...csvRows]
    .map((line) => line.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `my-shares-${format(new Date(), "yyyy-MM-dd")}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function MetricCard({
  title,
  value,
  amount,
  helper,
  icon: Icon,
  tone,
}: {
  title: string;
  value: string;
  amount: string;
  helper: string;
  icon: any;
  tone: "green" | "blue" | "orange" | "purple";
}) {
  const tones = {
    green: "border-green-100 bg-green-50/25 text-green-700",
    blue: "border-blue-100 bg-blue-50/25 text-blue-700",
    orange: "border-orange-100 bg-orange-50/25 text-orange-600",
    purple: "border-purple-100 bg-purple-50/25 text-purple-700",
  };

  return (
    <Card className={cn("rounded-lg border shadow-sm", tones[tone])}>
      <CardContent className="flex items-center gap-4 p-5">
        <div className={cn("flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white", tones[tone])}>
          <Icon className="h-6 w-6" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-1">
            <p className="text-sm font-semibold text-slate-900">{title}</p>
            <Info className="h-3.5 w-3.5 text-slate-400" />
          </div>
          <p className="mt-1 text-2xl font-bold">{value}</p>
          <p className="text-sm font-semibold text-slate-900">{amount}</p>
          <p className="mt-1 text-sm text-slate-500">{helper}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function SideSummaryCard({
  title,
  shares,
  amount,
  helper,
  icon: Icon,
  tone,
}: {
  title: string;
  shares: number;
  amount: number;
  helper: string;
  icon: any;
  tone: "blue" | "green" | "purple";
}) {
  const toneClass = {
    blue: "bg-blue-50 text-blue-700",
    green: "bg-green-50 text-green-700",
    purple: "bg-purple-50 text-purple-700",
  }[tone];

  return (
    <Card className="rounded-lg border shadow-sm">
      <CardContent className="flex gap-4 p-5">
        <div className={cn("flex h-11 w-11 shrink-0 items-center justify-center rounded-full", toneClass)}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-sm font-semibold text-slate-900">{title}</p>
          <p className={cn("mt-2 text-2xl font-bold", toneClass.split(" ")[1])}>{formatShares(shares)} Shares</p>
          <p className="mt-1 font-semibold text-slate-900">{formatMoney(amount)}</p>
          <p className="mt-1 text-sm text-slate-500">{helper}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export default function MyShares() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState<"all" | ShareActivityKind>("all");
  const [statusFilter, setStatusFilter] = useState<"all" | ShareStatus>("all");
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>([{ id: "rawDate", desc: true }]);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 8 });

  const { data: settings } = useQuery(
    ["my-shares-settings"],
    async () => {
      const { data } = await api.get("/settings");
      return data.data || data;
    },
    { staleTime: 300000, retry: 1 }
  );

  const { data: contributions = [], isLoading: loadingContributions } = useQuery(
    ["my-shares-contributions", user?.id],
    async () => {
      const { data } = await api.get("/contributions/my?limit=2000");
      const result = data.data || data;
      return Array.isArray(result) ? result : result?.contributions || [];
    },
    { enabled: !!user?.id, staleTime: 30000, retry: 1 }
  );

  const { data: memberPayments = [], isLoading: loadingPayments } = useQuery(
    ["my-shares-member-payments", user?.id],
    async () => {
      const { data } = await api.get("/member-payments/my");
      return Array.isArray(data.data) ? data.data : [];
    },
    { enabled: !!user?.id, staleTime: 30000, retry: 1 }
  );

  const shareValue = asNumber(settings?.savings?.shareValue) || asNumber((user as any)?.group?.pricePerShare);

  const activities = useMemo<ShareActivity[]>(() => {
    const contributionRows = (contributions || [])
      .map((item: any): ShareActivity | null => {
        const amount = asNumber(item?.amount ?? item?.depositAmount ?? item?.declaredAmount);
        if (amount <= 0) return null;
        const kind: ShareActivityKind = isVoluntarySaving(item) ? "saving" : "contribution";
        const rawDate = getActivityDate(item);
        const year = item?.year || new Date(rawDate).getFullYear();
        const month =
          typeof item?.month === "number" ? format(new Date(Number(year) || new Date().getFullYear(), item.month, 1), "MMMM") : item?.month;

        return {
          id: `contribution-${item?.id ?? item?._id ?? rawDate}`,
          date: formatDateLabel(rawDate),
          description:
            item?.description ||
            item?.notes ||
            (kind === "saving" ? "Savings Deposit" : `Monthly Contribution${month ? ` - ${month} ${year}` : ""}`),
          type: kind,
          shares: shareValue > 0 ? amount / shareValue : 0,
          amount,
          source: item?.paymentMethod || item?.source || (kind === "saving" ? "Member Payment" : "Salary Deduction"),
          status: normalizeStatus(item?.status || (item?.isVerified ? "Verified" : "")),
          approvedBy: getApprovedBy(item),
          reference: item?.reference || item?.referenceNumber || item?.code || `CON-${item?.id ?? "-"}`,
          rawDate: new Date(rawDate).getTime() || 0,
        };
      })
      .filter(Boolean) as ShareActivity[];

    const pendingPaymentRows = (memberPayments || [])
      .filter((payment: any) => {
        const status = normalizeStatus(payment?.status);
        const type = String(payment?.type || payment?.paymentType || "").toLowerCase();
        return status !== "Approved" && (type.includes("saving") || type.includes("contribution"));
      })
      .map((payment: any): ShareActivity => {
        const amount = asNumber(payment?.amount);
        const rawDate = getActivityDate(payment);
        return {
          id: `payment-${payment?.id ?? payment?._id ?? rawDate}`,
          date: formatDateLabel(rawDate),
          description: payment?.description || payment?.notes || "Pending Savings Payment",
          type: "saving",
          shares: shareValue > 0 ? amount / shareValue : 0,
          amount,
          source: payment?.paymentMethod || payment?.method || "Member Payment",
          status: normalizeStatus(payment?.status),
          approvedBy: "-",
          reference: payment?.reference || payment?.referenceNumber || `PAY-${payment?.id ?? "-"}`,
          rawDate: new Date(rawDate).getTime() || 0,
        };
      });

    return [...contributionRows, ...pendingPaymentRows].sort((a, b) => b.rawDate - a.rawDate);
  }, [contributions, memberPayments, shareValue]);

  const approvedActivities = activities.filter((activity) => activity.status === "Approved");
  const contributionAmount = approvedActivities
    .filter((activity) => activity.type === "contribution")
    .reduce((sum, activity) => sum + activity.amount, 0);
  const savingsAmount = approvedActivities
    .filter((activity) => activity.type === "saving")
    .reduce((sum, activity) => sum + activity.amount, 0);
  const contributionShares = shareValue > 0 ? contributionAmount / shareValue : 0;
  const savingsShares = shareValue > 0 ? savingsAmount / shareValue : 0;
  const totalShares = contributionShares + savingsShares;
  const totalAmount = contributionAmount + savingsAmount;
  const savingsPercent = totalShares > 0 ? Math.round((savingsShares / totalShares) * 1000) / 10 : 0;
  const contributionPercent = totalShares > 0 ? Math.round((contributionShares / totalShares) * 1000) / 10 : 0;

  const filteredActivities = useMemo(() => {
    return activities.filter((activity) => {
      if (tab !== "all" && activity.type !== tab) return false;
      if (statusFilter !== "all" && activity.status !== statusFilter) return false;
      return true;
    });
  }, [activities, statusFilter, tab]);

  const paginatedActivities = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize;
    return filteredActivities.slice(start, start + pagination.pageSize);
  }, [filteredActivities, pagination.pageIndex, pagination.pageSize]);

  const columns = useMemo<ColumnDef<ShareActivity>[]>(
    () => [
      {
        accessorKey: "rawDate",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Date" />,
        cell: ({ row }) => <span className="whitespace-nowrap text-sm text-slate-700">{row.original.date}</span>,
      },
      {
        accessorKey: "description",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Description" />,
        cell: ({ row }) => <span className="font-medium text-slate-800">{row.original.description}</span>,
      },
      {
        accessorKey: "type",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Type" />,
        cell: ({ row }) => (
          <Badge
            variant="outline"
            className={cn(
              "rounded-md",
              row.original.type === "saving"
                ? "border-green-200 bg-green-50 text-green-700"
                : "border-blue-200 bg-blue-50 text-blue-700"
            )}
          >
            {row.original.type === "saving" ? "Savings" : "Contribution"}
          </Badge>
        ),
      },
      {
        accessorKey: "shares",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Shares" />,
        cell: ({ row }) => <span className="font-semibold">{formatShares(row.original.shares)} Shares</span>,
      },
      {
        accessorKey: "amount",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Amount (FRW)" />,
        cell: ({ row }) => <span className="font-semibold">{Math.round(row.original.amount).toLocaleString()}</span>,
      },
      {
        accessorKey: "source",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Source" />,
        cell: ({ row }) => <span className="text-slate-600">{row.original.source}</span>,
      },
      {
        accessorKey: "status",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
        cell: ({ row }) => (
          <Badge
            variant="outline"
            className={cn(
              "rounded-md",
              row.original.status === "Approved" && "border-green-200 bg-green-50 text-green-700",
              row.original.status === "Pending" && "border-amber-200 bg-amber-50 text-amber-700",
              row.original.status === "Rejected" && "border-red-200 bg-red-50 text-red-700"
            )}
          >
            {row.original.status}
          </Badge>
        ),
      },
      {
        accessorKey: "approvedBy",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Approved By" />,
        cell: ({ row }) => <span className="text-slate-600">{row.original.approvedBy}</span>,
      },
      {
        id: "actions",
        header: "Actions",
        cell: () => (
          <Button variant="outline" size="sm" className="h-8 px-2" onClick={() => navigate("/member/reports/statement")}>
            <Eye className="h-4 w-4" />
          </Button>
        ),
      },
    ],
    [navigate]
  );

  const tabButton = (value: "all" | ShareActivityKind, label: string) => (
    <button
      type="button"
      onClick={() => {
        setTab(value);
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      }}
      className={cn(
        "border-b-2 px-3 py-3 text-sm font-semibold transition",
        tab === value ? "border-green-600 text-green-700" : "border-transparent text-slate-500 hover:text-slate-900"
      )}
    >
      {label}
    </button>
  );

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-950">My Shares</h1>
        <p className="mt-1 text-slate-600">Overview of all your shares from contributions and savings</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          title="Total Shares (All)"
          value={`${formatShares(totalShares)} Shares`}
          amount={formatMoney(totalAmount)}
          helper="Total value of all shares"
          icon={Layers}
          tone="green"
        />
        <MetricCard
          title="Shares from Contributions"
          value={`${formatShares(contributionShares)} Shares`}
          amount={formatMoney(contributionAmount)}
          helper="From monthly contributions"
          icon={Landmark}
          tone="blue"
        />
        <MetricCard
          title="Shares from Savings"
          value={`${formatShares(savingsShares)} Shares`}
          amount={formatMoney(savingsAmount)}
          helper="From voluntary savings"
          icon={PiggyBank}
          tone="orange"
        />
        <MetricCard
          title="Withdrawable (Savings Only)"
          value={`${formatShares(savingsShares)} Shares`}
          amount={formatMoney(savingsAmount)}
          helper="You can withdraw this amount"
          icon={Wallet}
          tone="purple"
        />
      </div>

      <div className="flex items-start gap-3 rounded-lg border border-blue-100 bg-blue-50/50 px-5 py-4 text-slate-700">
        <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white">
          <Info className="h-4 w-4" />
        </div>
        <div>
          <p className="font-semibold text-slate-900">Only shares from savings are withdrawable.</p>
          <p className="mt-1 text-sm">Shares from contributions are locked and cannot be withdrawn.</p>
          {shareValue <= 0 ? (
            <p className="mt-2 text-sm font-semibold text-amber-700">Share value is not configured, so share counts cannot be calculated.</p>
          ) : null}
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-[1fr_1fr_340px]">
        <Card className="rounded-lg border shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">Shares Breakdown</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center gap-6 sm:flex-row">
            <div
              className="grid h-44 w-44 shrink-0 place-items-center rounded-full"
              style={{
                background:
                  totalShares > 0
                    ? `conic-gradient(#22c55e 0 ${savingsPercent}%, #2563eb ${savingsPercent}% 100%)`
                    : "conic-gradient(#e5e7eb 0 100%)",
              }}
            >
              <div className="grid h-28 w-28 place-items-center rounded-full bg-white text-center shadow-inner">
                <div>
                  <p className="text-2xl font-bold text-slate-950">{formatShares(totalShares)}</p>
                  <p className="text-sm text-slate-500">Total Shares</p>
                </div>
              </div>
            </div>
            <div className="w-full space-y-5">
              <div className="flex items-start gap-3">
                <span className="mt-1 h-3 w-3 rounded-full bg-green-500" />
                <div>
                  <p className="font-semibold text-slate-900">From Savings</p>
                  <p className="text-sm text-slate-500">
                    {formatShares(savingsShares)} Shares ({savingsPercent}%)
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="mt-1 h-3 w-3 rounded-full bg-blue-600" />
                <div>
                  <p className="font-semibold text-slate-900">From Contributions</p>
                  <p className="text-sm text-slate-500">
                    {formatShares(contributionShares)} Shares ({contributionPercent}%)
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-lg border shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">Shares Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {[
              ["Total Shares (All)", `${formatShares(totalShares)} Shares`],
              ["Shares from Contributions", `${formatShares(contributionShares)} Shares`],
              ["Shares from Savings", `${formatShares(savingsShares)} Shares`],
              ["Total Value of All Shares", formatMoney(totalAmount)],
              ["Value from Contributions", formatMoney(contributionAmount)],
              ["Value from Savings", formatMoney(savingsAmount)],
            ].map(([label, value]) => (
              <div key={label} className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-b-0">
                <span className="text-slate-600">{label}</span>
                <span className="font-bold text-slate-950">{value}</span>
              </div>
            ))}
            <div className="flex items-center justify-between pt-2 text-green-700">
              <span className="font-semibold">Withdrawable (Savings Only)</span>
              <span className="font-bold">{formatMoney(savingsAmount)}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-lg border bg-green-50/20 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg text-green-800">About Shares</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-slate-600">
            <div className="flex gap-3">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
              <span>Every 1 Share = {shareValue > 0 ? formatMoney(shareValue) : "Not configured"}</span>
            </div>
            <div className="flex gap-3">
              <Layers className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
              <span>Both contributions and savings increase your total shares.</span>
            </div>
            <div className="flex gap-3">
              <Lock className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
              <span>Shares from contributions cannot be withdrawn.</span>
            </div>
            <div className="flex gap-3">
              <Wallet className="mt-0.5 h-4 w-4 shrink-0 text-orange-500" />
              <span>Shares from savings can be withdrawn anytime according to policy.</span>
            </div>
            <Button
              variant="outline"
              className="mt-2 w-full justify-between border-green-100 text-green-700 hover:bg-green-50"
              onClick={() => navigate("/member/support")}
            >
              <span className="inline-flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Shares Policy
              </span>
              <ArrowRight className="h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_300px]">
        <Card className="min-w-0 rounded-lg border shadow-sm">
          <CardContent className="p-0">
            <div className="flex flex-col gap-3 border-b px-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 overflow-x-auto">
                {tabButton("all", "All Activities")}
                {tabButton("contribution", "From Contributions")}
                {tabButton("saving", "From Savings")}
              </div>
              <div className="flex items-center gap-2 py-3">
                <Select
                  value={statusFilter}
                  onValueChange={(value) => {
                    setStatusFilter(value as "all" | ShareStatus);
                    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                  }}
                >
                  <SelectTrigger className="h-9 w-[150px]">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="Approved">Approved</SelectItem>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="Rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="icon" className="h-9 w-9">
                  <Filter className="h-4 w-4" />
                </Button>
                <Button className="h-9 bg-green-700 hover:bg-green-800" onClick={() => exportRows(filteredActivities)}>
                  <Download className="mr-2 h-4 w-4" />
                  Export
                </Button>
              </div>
            </div>
            <DataTable
              columns={columns}
              data={paginatedActivities}
              isLoading={loadingContributions || loadingPayments}
              columnFilters={columnFilters}
              setColumnFilters={setColumnFilters}
              sorting={sorting}
              setSorting={setSorting}
              setPagination={setPagination}
              pageIndex={pagination.pageIndex}
              pageSize={pagination.pageSize}
              pageCount={Math.max(1, Math.ceil(filteredActivities.length / pagination.pageSize))}
              enableExport={false}
              emptyMessage="No share activity found"
              emptyDescription="Approved contributions and savings will appear here."
            />
          </CardContent>
        </Card>

        <div className="space-y-4">
          <SideSummaryCard
            title="Contribution Shares (Locked)"
            shares={contributionShares}
            amount={contributionAmount}
            helper="Cannot be withdrawn"
            icon={Lock}
            tone="blue"
          />
          <SideSummaryCard
            title="Savings Shares (Withdrawable)"
            shares={savingsShares}
            amount={savingsAmount}
            helper="You can withdraw this amount"
            icon={Wallet}
            tone="green"
          />
          <SideSummaryCard
            title="Total Shares (All)"
            shares={totalShares}
            amount={totalAmount}
            helper="From all contributions & savings"
            icon={Layers}
            tone="purple"
          />
        </div>
      </div>
    </div>
  );
}
